import { Tooltip } from "@material-ui/core";
import _ from "lodash";
import React from "react";
import {
  LoadTransformationData,
  transformationOperationIcons,
  transformationOperationText,
  TransformationOperationType,
} from "./types";

import { beautifyBuiltinPropName } from "../util";
import { getBuiltinPropExampleValue } from "./sendBuiltinExamples";

/**
 * An abstract class for a transformation operation
 */
class TransformationOperationBase {
  // @ts-ignore
  operationType: TransformationOperationType;
  operandValue: string | number | boolean | null;
  targetValue: string | null;

  constructor(props: LoadTransformationData) {
    if (new.target === TransformationOperationBase) {
      throw new TypeError("Cannot construct Abstract instances directly");
    }
    const { targetValue, operandValue } = props;

    this.targetValue = targetValue;
    this.operandValue = operandValue;
  }

  shortName() {
    return transformationOperationText[this.operationType];
  }

  icon() {
    return transformationOperationIcons[this.operationType];
  }

  // An example property that could be targeted by this operation
  exampleTargetedProperty = {};

  /**
   * Perform this operation
   * @param {string} eventName - Incoming event name
   * @param {{}} eventProperties - Incoming event props
   * @returns {[string, Record<string, any>, Record<string, string>]}
   */
  perform(
    eventName: string,
    eventProperties = {}
  ): [string, Record<string, any>, Record<string, string>] {
    return [eventName, eventProperties, {}];
  }
}

/**
 * An Event Name replacement transformation operation
 */
export class ReplaceEventNameTransformationOperation extends TransformationOperationBase {
  operationType = "replaceName" as TransformationOperationType;
  manipulateMatchedProperty = (value: any, key: string) => ({
    [key]: undefined,
  });

  /**
   * An example of the resulting event name.
   * @param {string | null} eventDefName
   * @returns {JSX.Element}
   */
  exampleResultDisplay(eventDefName: string | null) {
    const title = this.targetValue || eventDefName || "add_to_cart";
    return (
      <>
        <Tooltip title={title}>
          <code className="">{title}</code>
        </Tooltip>{" "}
        &rarr;
        <code>{this.operandValue}</code>
      </>
    );
  }

  perform(
    eventName: string,
    eventProperties = {}
  ): [string, Record<string, any>, Record<string, string>] {
    return [this.operandValue as string, eventProperties, {}];
  }
}

export class SQLTransformationOperation extends TransformationOperationBase {
  operationType = "sqlTransformation" as TransformationOperationType;
  exampleResultDisplay() {
    return <></>;
  }
}

export class PostgresSQLTransformationOperation extends TransformationOperationBase {
  operationType = "postgresSQLTransformation" as TransformationOperationType;
  exampleResultDisplay() {
    return <></>;
  }
}

export class SendBuiltinPropertyTransformationOperation extends TransformationOperationBase {
  operationType = "sendBuiltinProperty" as TransformationOperationType;

  exampleResultDisplay() {
    let builtinPropName;
    if (this.targetValue) {
      builtinPropName = beautifyBuiltinPropName(this.targetValue);
    }

    const operandValue = this.operandValue || "";

    return (
      <>
        <Tooltip title={builtinPropName}>
          <code className="">{builtinPropName}</code>
        </Tooltip>
        &rarr;
        <Tooltip title={operandValue}>
          <code className="">{this.operandValue}</code>
        </Tooltip>
      </>
    );
  }

  perform(
    eventName: string,
    eventProperties = {}
  ): [string, Record<string, any>, Record<string, string>] {
    const outputProperties: Record<string, any> = {
      ...eventProperties,
      [`${this.operandValue}`]: getBuiltinPropExampleValue(this.targetValue),
    };
    return [eventName, outputProperties, {}];
  }
}

export class SendConstantPropertyTransfomationOperation extends TransformationOperationBase {
  operationType = "sendConstantProperty" as TransformationOperationType;

  exampleResultDisplay() {
    const operandValue = this.operandValue || "";
    const targetValue = this.targetValue || "";
    return (
      <>
        <Tooltip title={targetValue}>
          <code className="">{this.targetValue}</code>
        </Tooltip>
        &rarr;
        <Tooltip title={operandValue}>
          <code className="">{this.operandValue}</code>
        </Tooltip>
      </>
    );
  }

  perform(
    eventName: string,
    eventProperties = {}
  ): [string, Record<string, any>, Record<string, string>] {
    let constProp;
    if (this.targetValue) {
      try {
        constProp = JSON.parse(this.targetValue);
      } catch {
        return [eventName, eventProperties, {}];
      }
    }
    const outputProperties: Record<string, any> = {
      ...eventProperties,
      [`${this.operandValue}`]: constProp,
    };
    return [eventName, outputProperties, {}];
  }
}

/**
 * An abstract class for a property transformation
 */
export class PropertyTransformationOperationBase extends TransformationOperationBase {
  // A function to perform on a matched property.
  manipulateMatchedProperty = (
    propertyValue: any,
    propertyKey: string,
    modified: Record<string, string>
  ) => ({ [propertyKey]: propertyValue });

  /**
   * Iterate over properties and perform the operation on any needed props.
   * @param {object} properties
   * @returns {[object, Record<string, string>]}
   */
  performOnProperties(properties: object): [object, Record<string, string>] {
    // A list of modified keys
    let renamedProperties: Record<string, string> = {};
    let props = {};

    for (const [propertyKey, propertyValue] of Object.entries(properties)) {
      let modifiedObj = { [propertyKey]: propertyValue };

      // This property matches
      if (propertyKey === this.targetValue) {
        // Perform the property manipulation
        modifiedObj = this.manipulateMatchedProperty(
          propertyValue,
          propertyKey,
          renamedProperties
        );
      }

      props = _.merge(props, modifiedObj);
    }

    // NOTE(bianchi): removing values that are marked as undefined (ie. by the delete tranformation)
    props = _.pickBy(props, (val, _key) => !_.isUndefined(val));
    return [props, renamedProperties];
  }

  perform(
    eventName: string,
    eventProperties = {}
  ): [string, Record<string, any>, Record<string, string>] {
    const [props, changes] = this.performOnProperties(eventProperties);
    return [eventName, props, changes];
  }

  /**
   * An example of the resulting operation. Used for the UI.
   * @param {string | null} eventDefName
   * @returns {JSX.Element}
   */
  exampleResultDisplay(eventDefName: string | null) {
    return <code>{this.targetValue}</code>;
  }
}

/**
 * A transformation operation for deleting properties
 */
export class DeletePropertyTransformationOperation extends PropertyTransformationOperationBase {
  operationType = "deleteProperty" as TransformationOperationType;
  exampleTargetedProperty = { [this.targetValue as string]: "555-555-5555" };
  manipulateMatchedProperty = (propertyValue: any, propertyKey: string) => ({
    [propertyKey]: undefined,
  });
}

/**
 * A transformation operation for upper-casing property values
 */
export class UppercasePropertyTransformationOperation extends PropertyTransformationOperationBase {
  operationType = "uppercasePropertyValue" as TransformationOperationType;
  exampleTargetedProperty = { [this.targetValue as string]: "usd" };
  manipulateMatchedProperty = (propertyValue: any, propertyKey: string) => ({
    [propertyKey]: _.toString(propertyValue).toUpperCase(),
  });
}

/**
 * A transformation operation for lower-casing property values
 */
export class LowercasePropertyTransformationOperation extends PropertyTransformationOperationBase {
  operationType = "lowercasePropertyValue" as TransformationOperationType;
  exampleTargetedProperty = { [this.targetValue as string]: "TestValue" };
  manipulateMatchedProperty = (propertyValue: any, propertyKey: string) => ({
    [propertyKey]: _.toString(propertyValue).toLowerCase(),
  });
}

/**
 * A transformation operation for replacing property key
 */
export class ReplacePropertyTransformationOperation extends PropertyTransformationOperationBase {
  operationType = "replacePropertyKey" as TransformationOperationType;
  exampleTargetedProperty = { [this.targetValue as string]: 317823 };
  manipulateMatchedProperty = (
    propertyValue: any,
    propertyKey: string,
    renamedProperties: Record<string, string>
  ) => {
    renamedProperties[propertyKey] = this.operandValue as string;
    return { [this.operandValue as string]: propertyValue };
  };

  exampleResultDisplay(eventDefName: string | null): JSX.Element {
    return (
      <>
        <Tooltip title={this.targetValue || ""}>
          <code className="">{this.targetValue}</code>
        </Tooltip>{" "}
        &rarr;
        <code>{this.operandValue}</code>
      </>
    );
  }
}

/**
 * A transformation operation for casting property values.
 * Note: Honestly, this could maybe be three different classes, but that would be a little confusing because in the
 * db, these are all `castPropertyValue` transformations.
 */
export class CastPropertyTransformationOperation extends PropertyTransformationOperationBase {
  operationType = "castPropertyValue" as TransformationOperationType;
  exampleTargetedProperty = this.makeExampleTargetedProperty();
  manipulateMatchedProperty = (propertyValue: any, propertyKey: string) => ({
    [propertyKey]: this.castToValue(propertyValue),
  });

  makeExampleTargetedProperty() {
    switch (this.operandValue) {
      case "number":
        // @ts-ignore
        if (
          this.targetValue &&
          _.some(
            _.map(
              ["ltv", "amount", "value", "price", "total", "revenue"],
              (s) =>
                this.targetValue &&
                this.targetValue.toLowerCase().indexOf(s) !== -1
            )
          )
        ) {
          return { [this.targetValue as string]: "812.37" };
        }
        return { [this.targetValue as string]: "14" };
      case "boolean":
        return { [this.targetValue as string]: "t" };
      case "string":
        return { [this.targetValue as string]: false };
    }
    return {};
  }

  castToValue(value: any) {
    switch (this.operandValue) {
      case "number":
        try {
          return parseFloat(value);
        } catch {
          return this.operandValue;
        }

      case "string":
        return _.toString(value);

      case "boolean":
        if (_.isBoolean(value)) {
          // Already a bool
          return value;
        } else if (_.isNumber(value)) {
          // Handles numbers
          return value;
        } else if (_.isEmpty(value)) {
          // Handles empty string, empty objects, empty array
          return value;
        } else if (_.isString(value)) {
          // Strings with length > 0
          if (value.toLowerCase() === "t" || value.toLowerCase() === "true") {
            return true;
          }
          if (value.toLowerCase() === "f" || value.toLowerCase() === "false") {
            return false;
          }
          return value;
        }
        // It's either an object or an array with length > 0
        return true;
    }
  }

  exampleResultDisplay(eventDefName: string | null) {
    let val: React.ReactNode = "";
    switch (this.operandValue) {
      case "number":
        val = <em>Number</em>;
        break;
      case "boolean":
        val = <em>True/False</em>;
        break;
      case "string":
        val = <em>Text</em>;
        break;
    }
    return (
      <>
        <code>{this.targetValue}</code> to {val}
      </>
    );
  }
}

export type PossibleOperationClass =
  | CastPropertyTransformationOperation
  | LowercasePropertyTransformationOperation
  | UppercasePropertyTransformationOperation
  | ReplacePropertyTransformationOperation
  | ReplaceEventNameTransformationOperation
  | DeletePropertyTransformationOperation
  | SendBuiltinPropertyTransformationOperation
  | SQLTransformationOperation;

export class TransformationOperationFactory {
  static create(props: LoadTransformationData): PossibleOperationClass {
    switch (props.operation) {
      case "castPropertyValue":
        return new CastPropertyTransformationOperation(props);
      case "lowercasePropertyValue":
        return new LowercasePropertyTransformationOperation(props);
      case "uppercasePropertyValue":
        return new UppercasePropertyTransformationOperation(props);
      case "replacePropertyKey":
        return new ReplacePropertyTransformationOperation(props);
      case "deleteProperty":
        return new DeletePropertyTransformationOperation(props);
      case "replaceName":
        return new ReplaceEventNameTransformationOperation(props);
      case "sendBuiltinProperty":
        return new SendBuiltinPropertyTransformationOperation(props);
      case "sendConstantProperty":
        return new SendConstantPropertyTransfomationOperation(props);
      case "sqlTransformation":
        return new SQLTransformationOperation(props);
      case "postgresSQLTransformation":
        return new PostgresSQLTransformationOperation(props);
    }
  }
}
